import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchJson } from "@/lib/api";

export type RequestType = "mijenja" | "vraca" | "mijenja_dodatno";

export const MAX_RETURN_ITEMS = 10;

export type ReturnItem = {
  model: string;
  color: string;
  size: string;
  exchangeModel?: string;
  exchangeColor?: string;
  exchangeSize?: string;
};

export type AdditionalItem = {
  model: string;
  color: string;
  size: string;
};

export type ReturnRequest = {
  id: string;
  storeId: string;
  referenceNumber: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  orderNumber: string;
  requestType: RequestType;
  items: ReturnItem[];
  additionalItems: AdditionalItem[];
  /** @deprecated use items[0] */
  model: string;
  /** @deprecated use items[0] */
  color: string;
  /** @deprecated use items[0] */
  size: string;
  /** @deprecated use items[0] */
  exchangeModel: string;
  /** @deprecated use items[0] */
  exchangeColor: string;
  /** @deprecated use items[0] */
  exchangeSize: string;
  reasons: string[];
  declaration: boolean;
  bankAccount: string;
  deliveryAddress: string;
  deliveryCity: string;
  deliveryPostalCode: string;
  status: string;
  adminNote: string;
  rejectionReason: string;
  linkedOrderId: string;
  emailStatus: string;
  emailSentAt: number | null;
  emailError: string;
  createdAt: number;
  updatedAt: number;
  links?: ReturnItemLink[];
};

export type ReturnLinkRole = "returned" | "exchange" | "additional";

export type ReturnItemLink = {
  id: string;
  returnId: string;
  storeId: string;
  role: ReturnLinkRole;
  itemIndex: number;
  productId: number;
  variationId: number | null;
  productName: string;
  variationName: string;
  sku: string;
  /** True kad je povezani proizvod varijabilan. Uz `variationId: null` veza je nepotpuna. */
  isVariable: boolean;
  linkedBy: string | null;
  linkedAt: number;
};

export type ProductCandidate = {
  productId: number;
  productName: string;
  variationId: number | null;
  variationName: string;
  sku: string;
  stockStatus: string;
  stockQuantity: number | null;
  image: string | null;
  /** Varijabilan proizvod. `isVariable: true` uz `variationId: null` znači da varijacije
   *  nisu poznate — nije isto što i jednostavan proizvod. */
  isVariable: boolean;
  score: number;
};

/** Kandidat iz javne pretrage — bez internih podataka (količina na skladištu, ocjena podudaranja).
 *  `stockStatus` je tu samo da forma može označiti rasprodat proizvod; odabir ostaje moguć. */
export type PublicProductCandidate = {
  productId: number;
  productName: string;
  variationId: number | null;
  variationName: string;
  sku: string;
  isVariable: boolean;
  image: string | null;
  stockStatus?: string;
};

/** Veza na katalog koju je odabrao kupac; putuje uz stavku u tijelu zahtjeva. */
export type PublicItemLink = PublicProductCandidate;

/** Javna forma šalje samo odabrane proizvode — model, boju i veličinu server izvodi iz njih. */
export type PublicReturnItemInput = {
  link: PublicItemLink | null;
  exchangeLink?: PublicItemLink | null;
};

export type PublicAdditionalItemInput = { link: PublicItemLink | null };

export type CreatePublicReturnInput = {
  slug: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  orderNumber?: string;
  requestType: RequestType;
  items: PublicReturnItemInput[];
  additionalItems?: PublicAdditionalItemInput[];
  reasons: string[];
  declaration: boolean;
  bankAccount?: string;
  deliveryAddress?: string;
  deliveryCity?: string;
  deliveryPostalCode?: string;
};

export function useReturns(filters: { storeId?: string; status?: string; requestType?: string; search?: string; dateFrom?: string; dateTo?: string; page?: number; perPage?: number; stale?: boolean } = {}) {
  const params = new URLSearchParams();
  if (filters.storeId) params.set("store_id", filters.storeId);
  if (filters.status) params.set("status", filters.status);
  if (filters.requestType) params.set("request_type", filters.requestType);
  if (filters.search) params.set("search", filters.search);
  if (filters.dateFrom) params.set("date_from", filters.dateFrom);
  if (filters.dateTo) params.set("date_to", filters.dateTo);
  if (filters.page) params.set("page", String(filters.page));
  if (filters.perPage) params.set("per_page", String(filters.perPage));
  if (filters.stale) params.set("stale", "1");

  return useQuery({
    queryKey: ["returns", filters.storeId, filters.status, filters.requestType, filters.search, filters.dateFrom, filters.dateTo, filters.page, filters.perPage, filters.stale],
    queryFn: () => fetchJson<{ items: ReturnRequest[]; total: number }>(`/api/returns?${params}`),
  });
}

export type ReturnStats = {
  totalReturns: number;
  /** Zahtjevi u periodu koji još nisu zatvoreni. */
  unresolvedCount: number;
  /** Prosjek dana od zahtjeva do zatvaranja; `null` kad ništa nije zatvoreno u periodu. */
  avgResolutionDays: number | null;
  /** Jedan red po danu perioda, uključujući dane bez povrata. */
  trend: { date: string; count: number }[];
  byStatus: { status: string; count: number }[];
  byType: { requestType: string; count: number }[];
  byReason: { reason: string; count: number }[];
  /** Povezane stavke, grupisane po katalog proizvodu. */
  byProduct: { productId: number; name: string; count: number }[];
  /** Stavke bez veze — brojane po slobodnom tekstu modela. */
  byProductUnlinked: { model: string; count: number }[];
  byVariation: { productId: number; variationId: number; name: string; count: number }[];
  exchangeFlow: { from: string; to: string; count: number }[];
  staleCount: number;
};

export function useReturnStats(filters: { storeId?: string; days: number }) {
  const params = new URLSearchParams({ days: String(filters.days) });
  if (filters.storeId) params.set("store_id", filters.storeId);

  return useQuery({
    queryKey: ["returns", "stats", filters.storeId, filters.days],
    queryFn: () => fetchJson<ReturnStats>(`/api/returns/stats?${params}`),
  });
}

/** Samo brojač starih zahtjeva — lista ga treba za dugme filtera, bez cijele statistike. */
export function useStaleReturnsCount(storeId: string | null) {
  const params = new URLSearchParams();
  if (storeId) params.set("store_id", storeId);
  return useQuery({
    queryKey: ["returns", "staleCount", storeId],
    queryFn: () => fetchJson<{ count: number }>(`/api/returns/stale-count?${params}`),
  });
}

export type ReturnRateRow = {
  productId: number;
  name: string;
  returned: number;
  sold: number;
  /** `returned / sold`; 0 kad `sold === 0` — tada se prikazuje „—", ne 0%. */
  rate: number;
};

export type ReturnRateErrorCode = "not_webhook" | undefined;

async function fetchReturnRate(url: string): Promise<{ items: ReturnRateRow[] }> {
  const res = await fetch(url);
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    const e: any = new Error(body.error || res.statusText);
    e.code = body.code;
    throw e;
  }
  return res.json();
}

export function useReturnRate(filters: { storeId?: string; days: number }) {
  const params = new URLSearchParams({ days: String(filters.days) });
  if (filters.storeId) params.set("store_id", filters.storeId);

  return useQuery({
    queryKey: ["returns", "rate", filters.storeId, filters.days],
    queryFn: () => fetchReturnRate(`/api/returns/rate?${params}`),
    enabled: !!filters.storeId,
    // `not_webhook` je očekivano stanje, ne prolazna greška — nema smisla ponavljati.
    retry: (failureCount, error: any) => error?.code === undefined && failureCount < 2,
  });
}

export function useNewReturnsCount(storeId: string | null) {
  return useQuery({
    queryKey: ["returns", storeId, "new"],
    queryFn: async () => {
      const params = new URLSearchParams({ status: "new" });
      if (storeId) params.set("store_id", storeId);
      const result = await fetchJson<{ items: ReturnRequest[]; total: number }>(`/api/returns?${params}`);
      return result.total;
    },
    enabled: !!storeId,
  });
}

export function useReturn(id: string | null) {
  return useQuery({
    queryKey: ["return", id],
    queryFn: () => fetchJson<ReturnRequest>(`/api/returns/${id}`),
    enabled: !!id,
  });
}

export function useUpdateReturnStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, rejectionReason, sendEmail }: { id: string; status: string; rejectionReason?: string; sendEmail: boolean }) =>
      fetchJson<ReturnRequest>(`/api/returns/${id}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status, rejectionReason, sendEmail }),
      }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["returns"] });
      qc.invalidateQueries({ queryKey: ["return", variables.id] });
    },
  });
}

export function useLinkReturnOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, orderNumber, orderId }: { id: string; orderNumber: string; orderId: string }) =>
      fetchJson(`/api/returns/${id}/order-link`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderNumber, orderId }),
      }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["return", variables.id] });
    },
  });
}

export function useUnlinkReturnOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      fetchJson(`/api/returns/${id}/order-link`, { method: "DELETE" }),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["return", id] });
    },
  });
}

export function useUpdateReturnNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      fetchJson(`/api/returns/${id}/note`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note }),
      }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["return", variables.id] });
    },
  });
}

export type UpdateReturnCustomerInput = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  orderNumber: string;
  bankAccount: string;
  deliveryAddress: string;
  deliveryCity: string;
  deliveryPostalCode: string;
};

export function useUpdateReturnCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: UpdateReturnCustomerInput & { id: string }) =>
      fetchJson<ReturnRequest>(`/api/returns/${id}/customer`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["return", variables.id] });
      qc.invalidateQueries({ queryKey: ["returns"] });
    },
  });
}

export function useResendReturnEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      fetchJson<ReturnRequest>(`/api/returns/${id}/resend-email`, { method: "POST" }),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["return", id] });
      qc.invalidateQueries({ queryKey: ["returns"] });
    },
  });
}

export function useDeleteReturn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      fetchJson(`/api/returns/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["returns"] }),
  });
}

export function useReturnProductSearch(storeId: string | null, q: string) {
  const term = (q || "").trim();
  return useQuery({
    queryKey: ["returnProductSearch", storeId, term],
    queryFn: () => {
      const params = new URLSearchParams({ store_id: storeId || "", q: term });
      return fetchJson<{ candidates: ProductCandidate[] }>(`/api/returns/product-search?${params}`);
    },
    enabled: !!storeId && term.length >= 2,
  });
}

export function useLinkReturnItem(returnId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      role: ReturnLinkRole;
      itemIndex: number;
      productId: number;
      variationId?: number | null;
      productName: string;
      variationName?: string;
      sku?: string;
      isVariable?: boolean;
    }) =>
      fetchJson<{ link: ReturnItemLink }>(`/api/returns/${returnId}/links`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["return", returnId] });
      // Statistika povrata živi pod ["returns", "stats", ...] — prefiks pokriva i listu i statistiku.
      qc.invalidateQueries({ queryKey: ["returns"] });
    },
  });
}

export function useUnlinkReturnItem(returnId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ role, itemIndex }: { role: ReturnLinkRole; itemIndex: number }) =>
      fetchJson<{ ok: true }>(`/api/returns/${returnId}/links/${role}/${itemIndex}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["return", returnId] });
      qc.invalidateQueries({ queryKey: ["returns"] });
    },
  });
}

export function usePublicStoreBranding(slug: string | null) {
  return useQuery({
    queryKey: ["publicStore", slug],
    queryFn: () => fetchJson<{ name: string; slug: string; logoUrl: string; primaryColor: string; returnInstructionUrl: string }>(`/api/public/stores/${slug}`),
    enabled: !!slug,
  });
}

/** Javna pretraga kataloga s forme za povrat — bez prijave, ograničena po adresi na serveru.
 *  `inStockOnly` se koristi kad kupac bira šta će dobiti (zamjena, dodatna narudžba). */
export function usePublicProductSearch(
  slug: string | null,
  q: string,
  opts: { inStockOnly?: boolean } = {},
) {
  const term = (q || "").trim();
  const inStockOnly = opts.inStockOnly ?? false;
  return useQuery({
    queryKey: ["publicProductSearch", slug, term, inStockOnly],
    queryFn: () => {
      const params = new URLSearchParams({ q: term });
      if (inStockOnly) params.set("in_stock", "1");
      return fetchJson<{ candidates: PublicProductCandidate[] }>(
        `/api/public/returns/${slug}/product-search?${params}`,
      );
    },
    enabled: !!slug && term.length >= 2,
  });
}

export function useCreatePublicReturn() {
  return useMutation({
    mutationFn: ({ slug, ...body }: CreatePublicReturnInput) =>
      fetchJson<{ referenceNumber: string }>(`/api/public/returns/${slug}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
  });
}

export function usePublicReturnStatus(slug: string | null, ref: string | null) {
  return useQuery({
    queryKey: ["publicReturnStatus", slug, ref],
    queryFn: () => fetchJson<{ referenceNumber: string; status: string; requestType: RequestType; createdAt: number; updatedAt: number }>(`/api/public/returns/${slug}/status/${ref}`),
    enabled: !!slug && !!ref,
  });
}

export type ReturnOrderPrefillItem = {
  product_id: number;
  variation_id?: number;
  name: string;
  sku: string;
  price: string;
  image?: string;
  source: "exchange" | "additional";
};

export type ReturnOrderPrefillUnlinked = { source: "exchange" | "additional"; label: string };

/** Ista polja kao `Address` u `src/pages/orders/new.tsx` — prefill se spreada u to stanje,
 *  pa `Record<string, string>` ovdje ne bi prošao TypeScript. */
export type ReturnOrderPrefillAddress = {
  first_name: string;
  last_name: string;
  company: string;
  address_1: string;
  address_2: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  email: string;
  phone: string;
};

export type ReturnOrderPrefill = {
  returnId: string;
  referenceNumber: string;
  storeId: string;
  requestType: RequestType;
  reasons: string[];
  billing: ReturnOrderPrefillAddress;
  shipping: ReturnOrderPrefillAddress;
  items: ReturnOrderPrefillItem[];
  unlinkedItems: ReturnOrderPrefillUnlinked[];
};

export type CreateOrderFromReturnResult = {
  order: any;
  linked: boolean;
  linkError?: string;
  noteError?: string;
  /** Postavljen samo kad stvarni `order.total` odstupa od očekivanog za više od 0.01 KM. */
  totalMismatch?: { expected: number; actual: number };
};

export function useReturnOrderPrefill(returnId: string | null) {
  return useQuery({
    queryKey: ["return-order-prefill", returnId],
    queryFn: () => fetchJson<ReturnOrderPrefill>(`/api/returns/${returnId}/order-prefill`),
    enabled: !!returnId,
    // Cijene i veze se mijenjaju izvan aplikacije — prefill se uvijek čita svjež.
    staleTime: 0,
  });
}

export function useCreateOrderFromReturn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
      priceDifference,
      shippingCharged,
      shippingCost,
    }: {
      id: string;
      payload: any;
      priceDifference: number;
      shippingCharged: boolean;
      shippingCost: number;
    }) =>
      fetchJson<CreateOrderFromReturnResult>(`/api/returns/${id}/order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ payload, priceDifference, shippingCharged, shippingCost }),
      }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["return", variables.id] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["returns"] });
    },
  });
}
